import { IDIOMAS, buscarIdioma, idiomaPorOmision, type Idioma } from './idiomas';

/**
 * Elección de idioma: qué se guarda, cómo se detecta y quién gana.
 *
 * La regla es una sola: **una elección explícita manda para siempre**. Quien
 * llega con el navegador en inglés y decide leer el sitio en español no debe
 * volver a encontrárselo en inglés en la siguiente visita. Por eso solo se
 * detecta cuando no hay nada guardado.
 */

export const CLAVE_IDIOMA = 'ilumetria.idioma';

/**
 * `localStorage` no siempre existe ni siempre deja escribir: en modo privado de
 * Safari, con cookies bloqueadas o dentro de un iframe restringido, *lanza*.
 * Un sitio que no abre porque no pudo recordar un idioma sería absurdo, así que
 * todo acceso pasa por aquí y falla en silencio.
 */
function leerGuardado(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_IDIOMA);
  } catch {
    return null;
  }
}

export function guardarIdioma(codigo: string): void {
  try {
    window.localStorage.setItem(CLAVE_IDIOMA, codigo);
  } catch {
    /* Sin persistencia el idioma dura lo que la pestaña. Es degradar, no fallar. */
  }
}

/** El idioma que la persona eligió a mano, si alguna vez lo hizo. */
export function idiomaGuardado(): Idioma | undefined {
  return buscarIdioma(leerGuardado());
}

/**
 * Primer idioma del navegador que el sitio hable.
 *
 * `navigator.languages` viene en orden de preferencia y con región
 * (`en-GB`, `es-419`), así que se compara contra el código base. Se recorre
 * completo en vez de mirar solo el primero: alguien con `['de', 'en', 'es']`
 * no habla alemán con nosotros, pero sí inglés.
 */
export function idiomaDelNavegador(): Idioma | undefined {
  const preferidos =
    typeof navigator === 'undefined'
      ? []
      : (navigator.languages ?? [navigator.language]).filter(Boolean);

  for (const preferido of preferidos) {
    const base = preferido.toLowerCase().split('-')[0];
    const encontrado = IDIOMAS.find((idioma) => idioma.codigo === base);
    if (encontrado) return encontrado;
  }
  return undefined;
}

/** Guardado → navegador → español. En ese orden y sin excepciones. */
export function detectarIdioma(): Idioma {
  return idiomaGuardado() ?? idiomaDelNavegador() ?? idiomaPorOmision();
}
